import Close from 'assets/icons/Close';
import FilterAccordion from 'components/accordions/FilterAccordion';
import Heading from 'components/text/Heading';
import PropTypes from 'prop-types';
import React from 'react';
import SortOptions from 'components/Filter/SortOptions';
import ThemeButton from 'components/buttons/ThemeButton';
import colors from 'config/theme/colors';
import { injectModel } from 'state';
import overlayProps from 'utils/proptypes/overlay';
import styled from 'libs/styled';
import useClientHeight from 'hooks/useClientHeight';
import { useTranslation } from 'react-i18next';

const Wrapper = styled('div')`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const FilterHeader = styled('div')`
    display: flex;
    justify-content: space-between;
    padding: 16px;
    flex-shrink: 0; /* Fix for safari iPhone */
    border-bottom: 1px solid ${colors.blackOpacityMedium};
    background-color: ${colors.background};

    @supports (position: sticky) {
        position: sticky;
        left: 0;
        top: 0;
        z-index: 1;
    }
`;

const FilterFooter = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: auto;
    padding: 16px 16px;
`;

const NoResultText = styled('span')`
    text-align: center;
    font-size: 1.4rem;
    color: ${colors.grey6};
    margin-top: 8px;
`;

const Filter = ({ overlay, className, filters = [], handleChange = {}, clear = () => null, hitsCount, sorts }) => {
    const { t } = useTranslation();
    const clientHeight = useClientHeight();

    return (
        <Wrapper className={className} height={clientHeight}>
            <FilterHeader>
                <Heading as="h3" fontSize="1.6rem">
                    {t('filter_config.filter_and_sort')}
                </Heading>
                <button type="button" onClick={() => overlay.hide()}>
                    <Close />
                </button>
            </FilterHeader>
            <SortOptions sorts={sorts} handleChange={handleChange.sort} />
            <FilterAccordion filters={filters} handleChange={handleChange.filter?.value} />
            <FilterFooter>
                <ThemeButton mb="8px" size="xl" theme="outlinedBlack" onClick={() => clear.filter()}>
                    {t('filter_config.clear_filter')}
                </ThemeButton>
                <ThemeButton size="xl" theme="black" onClick={() => overlay.hide()}>
                    {t('filter_config.show_results')} {`(${hitsCount})`}
                </ThemeButton>
                {hitsCount < 1 && <NoResultText>{t('filter_config.no_results_with_these_filters')}</NoResultText>}
            </FilterFooter>
        </Wrapper>
    );
};

Filter.propTypes = {
    className: PropTypes.string,
    clear: PropTypes.object,
    filters: PropTypes.arrayOf(
        PropTypes.exact({
            key: PropTypes.string,
            label: PropTypes.string,
            stats: PropTypes.object,
            totalSelected: PropTypes.number,
            values: PropTypes.array,
        })
    ),
    handleChange: PropTypes.shape({
        filter: PropTypes.shape({
            value: PropTypes.func,
            range: PropTypes.func,
        }),
        sort: PropTypes.func,
    }),
    hitsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    overlay: overlayProps.isRequired,
    sorts: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
};

export default injectModel('overlay')(Filter);
